(() => {
  /* Formatea centavos usando el money_format de la tienda, para que
     lo que pinta el JS se vea igual que lo que pinta Liquid. */
  const group = (value, decimals, sep, dot) => {
    const parts = value.toFixed(decimals).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return parts.length > 1 ? parts[0] + dot + parts[1] : parts[0];
  };

  const money = (cents, format) => {
    const v = (Number(cents) || 0) / 100;
    const fmt = format || '${{amount}}';
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(v, 0, ',', '.');
        case 'amount_with_comma_separator': return group(v, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(v, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(v, 2, "'", '.');
        default: return group(v, 2, ',', '.');
      }
    });
  };

  /* ---------- Dropdown de aroma ---------- */
  class KitSelect extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('.kit-select__trigger');
      this.panel = this.querySelector('.kit-select__panel');
      this.valueEl = this.querySelector('.kit-select__value');
      this.thumbEl = this.querySelector('.kit-select__thumb');
      this.options = Array.from(this.querySelectorAll('.kit-select__opt'));
      if (!this.trigger || !this.panel) return;

      this.trigger.addEventListener('click', () => this.toggle());
      this.trigger.addEventListener('keydown', (e) => this.onTriggerKey(e));
      this.panel.addEventListener('keydown', (e) => this.onPanelKey(e));

      this.options.forEach((opt) => {
        opt.addEventListener('click', () => this.pick(opt));
        opt.addEventListener('mousemove', () => this.setActive(opt));
      });

      // e.target === this ocurre al tocar el fondo oscuro, que es un pseudoelemento.
      this.onOutside = (e) => { if (!this.contains(e.target) || e.target === this) this.close(); };
    }

    get isOpen() { return this.dataset.open === 'true'; }

    get aroma() { return this.valueEl ? this.valueEl.textContent.trim() : ''; }

    toggle() { this.isOpen ? this.close() : this.open(); }

    /* Decide si el panel cabe abajo. Si no, lo voltea hacia arriba, y en
       cualquier caso lo limita al espacio real que queda en pantalla. */
    acomodarPanel() {
      // En movil el panel es una hoja fija al fondo: no hay que medir nada.
      if (getComputedStyle(this.panel).position === 'fixed') {
        this.dataset.drop = 'sheet';
        this.panel.style.maxHeight = '';
        return;
      }

      const r = this.trigger.getBoundingClientRect();
      const margen = 12;

      // La barra fija de compra ocupa la parte baja en movil.
      const barra = document.querySelector('[data-kit-bar]');
      let estorbo = 0;
      if (barra && getComputedStyle(barra).display !== 'none') {
        estorbo = barra.getBoundingClientRect().height;
      }

      const abajo = window.innerHeight - r.bottom - margen - estorbo;
      const arriba = r.top - margen;
      const volteado = abajo < 180 && arriba > abajo;

      this.dataset.drop = volteado ? 'up' : 'down';
      this.panel.style.maxHeight = `${Math.max(140, Math.floor(volteado ? arriba : abajo))}px`;
    }

    open() {
      this.dataset.open = 'true';
      this.panel.hidden = false;
      this.acomodarPanel();
      this.trigger.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.onOutside);
      const current = this.options.find((o) => o.getAttribute('aria-selected') === 'true') || this.options[0];
      this.setActive(current);
      this.panel.focus({ preventScroll: true });
    }

    close() {
      this.dataset.open = 'false';
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onOutside);
    }

    setActive(opt) {
      if (!opt) return;
      this.options.forEach((o) => o.removeAttribute('data-active'));
      opt.dataset.active = 'true';
      this.active = opt;
      opt.scrollIntoView({ block: 'nearest' });
    }

    move(step) {
      const usable = this.options.filter((o) => o.getAttribute('aria-disabled') !== 'true');
      if (!usable.length) return;
      const i = usable.indexOf(this.active);
      this.setActive(usable[(i + step + usable.length) % usable.length]);
    }

    onTriggerKey(e) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); this.open(); }
    }

    onPanelKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); this.close(); this.trigger.focus(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); this.move(1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); this.move(-1); }
      else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.pick(this.active); }
    }

    pick(opt) {
      if (!opt || opt.getAttribute('aria-disabled') === 'true') return;
      this.options.forEach((o) => o.setAttribute('aria-selected', 'false'));
      opt.setAttribute('aria-selected', 'true');

      const d = opt.dataset;
      if (this.valueEl) this.valueEl.textContent = d.aroma || '';
      if (this.thumbEl && d.img) this.thumbEl.src = d.img;

      this.close();
      this.trigger.focus();
      this.dispatchEvent(new CustomEvent('kit:change', { bubbles: true, detail: { ...d } }));
    }
  }

  /* ---------- Armador ---------- */
  class KitBuilder extends HTMLElement {
    connectedCallback() {
      this.pct = Number(this.dataset.discount || 0) / 100;
      this.code = this.dataset.discountCode || '';
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.fmt = this.dataset.moneyFormat || '${{amount}}';
      this.kitVariant = this.dataset.kitVariant || '';

      this.slots = Array.from(this.querySelectorAll('[data-slot]'));
      this.compareEl = this.querySelector('[data-kit-compare]');
      this.totalEl = this.querySelector('[data-kit-total]');
      this.saveEl = this.querySelector('[data-kit-save]');
      this.errorEl = this.querySelector('[data-kit-error]');
      this.buttons = Array.from(this.querySelectorAll('[data-kit-add]'));
      this.bar = this.querySelector('[data-kit-bar]');
      this.barWas = this.querySelector('[data-bar-was]');
      this.barNow = this.querySelector('[data-bar-now]');

      this.addEventListener('kit:change', (e) => this.onScent(e));
      this.buttons.forEach((b) => b.addEventListener('click', () => this.addToCart()));

      this.slots.forEach((slot) => this.paintSlot(slot));
      this.recalculate();
      this.watchBar();
    }

    onScent(e) {
      const slot = e.target.closest('[data-slot]');
      if (!slot) return;
      const d = e.detail;
      slot.dataset.variantId = d.variantId || '';
      slot.dataset.price = d.price || '0';
      slot.dataset.compare = d.compare || d.price || '0';
      slot.dataset.available = d.available || 'true';

      const img = slot.querySelector('[data-slot-img]');
      if (img && d.img) img.src = d.img;

      /* Si el bloque tiene nombre fijo en el editor, no lo sobrescribimos. */
      if (slot.dataset.fixedName !== 'true') {
        const name = slot.querySelector('[data-slot-name]');
        if (name && d.name) name.textContent = d.name;
      }

      this.paintSlot(slot);
      this.recalculate();
    }

    paintSlot(slot) {
      const compare = Number(slot.dataset.compare || 0);
      const now = Math.round(compare * (1 - this.pct));
      const was = slot.querySelector('[data-slot-was]');
      const nowEl = slot.querySelector('[data-slot-now]');
      if (was) was.textContent = money(compare, this.fmt);
      if (nowEl) nowEl.textContent = money(now, this.fmt);
    }

    recalculate() {
      const compare = this.slots.reduce((s, el) => s + Number(el.dataset.compare || 0), 0);
      const total = Math.round(compare * (1 - this.pct));
      const saved = compare - total;

      if (this.compareEl) this.compareEl.textContent = money(compare, this.fmt);
      if (this.totalEl) this.totalEl.textContent = money(total, this.fmt);
      if (this.saveEl) this.saveEl.textContent = saved > 0 ? `Ahorras ${money(saved, this.fmt)}` : '';
      if (this.barWas) this.barWas.textContent = money(compare, this.fmt);
      if (this.barNow) this.barNow.textContent = money(total, this.fmt);
    }

    watchBar() {
      const anchor = this.querySelector('[data-kit-anchor]');
      if (!this.bar || !anchor || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(
        ([entry]) => { this.bar.dataset.visible = entry.isIntersecting ? 'false' : 'true'; },
        { rootMargin: '0px 0px -40px 0px' }
      ).observe(anchor);
    }

    unavailable() {
      return this.slots.filter((el) => !el.dataset.variantId || el.dataset.available === 'false');
    }

    /* Con producto padre: una linea con los componentes en las propiedades.
       Sin producto padre: las seis lineas por separado. */
    items() {
      if (this.kitVariant) {
        const props = { _componentes: this.slots.map((el) => el.dataset.variantId).join(',') };
        this.slots.forEach((el) => {
          const select = el.querySelector('kit-select');
          if (!select) return;
          props[el.dataset.choiceLabel || 'Aroma'] = select.aroma;
        });
        return [{ id: Number(this.kitVariant), quantity: 1, properties: props }];
      }

      return this.slots.map((el) => ({
        id: Number(el.dataset.variantId),
        quantity: 1,
        properties: { _kit: 'kit-esenciales' },
      }));
    }

    setError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg || '';
      this.errorEl.hidden = !msg;
    }

    setBusy(on) {
      this.buttons.forEach((b) => {
        b.disabled = on;
        if (!b.dataset.label) b.dataset.label = b.textContent;
        b.textContent = on ? 'Agregando…' : b.dataset.label;
      });
    }

    /* Abre el Smart Cart de Rebuy si esta disponible. Si no, cae a la
       pagina de carrito de siempre. */
    async abrirCarrito() {
      const sc = window.Rebuy && window.Rebuy.SmartCart;

      // Con codigo de descuento hay que navegar para que se aplique.
      if (this.code) {
        window.location.href = `/discount/${encodeURIComponent(this.code)}?redirect=${encodeURIComponent(this.cartUrl)}`;
        return;
      }

      if (!sc || typeof sc.show !== 'function') {
        window.location.href = this.cartUrl;
        return;
      }

      try {
        const cart = await fetch('/cart.js').then((r) => r.json());
        if (typeof sc.setCartAsync === 'function') await sc.setCartAsync(cart);
        else if (typeof sc.setCart === 'function') sc.setCart(cart);
        if (typeof sc.updateCartCount === 'function') sc.updateCartCount();
        sc.show();
        this.setBusy(false);
      } catch (_) {
        window.location.href = this.cartUrl;
      }
    }

    async addToCart() {
      this.setError('');

      const faltantes = this.unavailable();
      if (faltantes.length) {
        const nombre = faltantes[0].dataset.choiceLabel || 'Uno de los productos';
        this.setError(`${nombre} no está disponible en el aroma que elegiste. Cambia esa opción para continuar.`);
        return;
      }

      this.setBusy(true);
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: this.items() }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.description || 'No pudimos agregar el kit.');
        }
        await this.abrirCarrito();
      } catch (err) {
        this.setError(err.message || 'No pudimos agregar el kit. Intenta de nuevo.');
        this.setBusy(false);
      }
    }
  }

  /* ---------- Contador ---------- */
  class KitTimer extends HTMLElement {
    connectedCallback() {
      this.limite = Date.parse(this.dataset.deadline || '');
      if (Number.isNaN(this.limite)) return; // fecha mal escrita: no mostramos nada raro

      this.reloj = this.querySelector('[data-timer-clock]');
      this.texto = this.querySelector('[data-timer-text]');
      this.fin = this.querySelector('[data-timer-ended]');
      this.d = this.querySelector('[data-timer-d]');
      this.diasWrap = this.querySelector('[data-timer-dias-wrap]');
      this.diasSep = this.querySelector('[data-timer-dias-sep]');
      this.h = this.querySelector('[data-timer-h]');
      this.m = this.querySelector('[data-timer-m]');
      this.s = this.querySelector('[data-timer-s]');

      this.tick();
      this.intervalo = setInterval(() => this.tick(), 1000);

      /* Si la pestana estuvo dormida, al volver puede haber expirado. */
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.tick();
      });
    }

    disconnectedCallback() {
      clearInterval(this.intervalo);
    }

    tick() {
      const restante = this.limite - Date.now();

      if (restante <= 0) {
        clearInterval(this.intervalo);
        this.terminar();
        return;
      }

      const total = Math.floor(restante / 1000);
      const dos = (n) => String(n).padStart(2, '0');

      /* Los dias solo aparecen si faltan. El ultimo dia el reloj vuelve a
         ser horas, minutos y segundos, que es como mas aprieta. */
      const dias = Math.floor(total / 86400);
      const hayDias = dias > 0;
      if (this.diasWrap) this.diasWrap.hidden = !hayDias;
      if (this.diasSep) this.diasSep.hidden = !hayDias;
      if (this.d) this.d.textContent = String(dias);

      const horas = hayDias ? Math.floor((total % 86400) / 3600) : Math.floor(total / 3600);
      if (this.h) this.h.textContent = dos(horas);
      if (this.m) this.m.textContent = dos(Math.floor((total % 3600) / 60));
      if (this.s) this.s.textContent = dos(total % 60);
    }

    terminar() {
      if (this.dataset.ended === 'true') return;
      this.dataset.ended = 'true';
      if (this.reloj) this.reloj.hidden = true;
      if (this.texto) this.texto.hidden = true;
      if (this.fin) this.fin.hidden = false;

      if (this.dataset.disableOnEnd === 'true') {
        document.querySelectorAll('[data-kit-add]').forEach((b) => {
          b.disabled = true;
          b.setAttribute('data-expirado', 'true');
        });
      }
    }
  }

  if (!customElements.get('kit-timer')) customElements.define('kit-timer', KitTimer);
  if (!customElements.get('kit-select')) customElements.define('kit-select', KitSelect);
  if (!customElements.get('kit-builder')) customElements.define('kit-builder', KitBuilder);

  /* ---------- FAQ ---------- */
  document.addEventListener('click', (e) => {
    const q = e.target.closest('.kit-faq__q');
    if (!q) return;
    const item = q.closest('.kit-faq__item');
    const open = item.dataset.open === 'true';
    item.dataset.open = open ? 'false' : 'true';
    q.setAttribute('aria-expanded', open ? 'false' : 'true');
    const answer = item.querySelector('.kit-faq__a');
    if (answer) answer.hidden = open;
  });
})();
